import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import ScrollToTop from "@/components/ScrollToTop";
import GatedRoute from "@/components/GatedRoute";
import { SessionControlProvider } from "@/contexts/SessionControlContext";
import SessionControlUI from "@/components/SessionControlModals";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import XpLevelBadge from "@/components/XpLevelBadge";
import Auth from "@/pages/NewAuth";
import VerifyEmail from "@/pages/VerifyEmail";
import Subscription from "@/pages/Subscription";
import Favorites from "@/pages/Favorites";
import Onboarding from "@/pages/Onboarding";
import DiagnosticTest from "@/pages/DiagnosticTest";
import LearningPlan from "@/pages/LearningPlan";
import Dashboard from "@/pages/Dashboard";
import MockExam from "@/pages/MockExam";
import Results from "@/pages/Results";
import CategorySelection from "@/pages/CategorySelection";
import SubjectDashboard from "@/pages/SubjectDashboard";
import ElevenPlusPatterns from "@/pages/ElevenPlusPatterns";
import PaperLibrary from "@/pages/PaperLibrary";
import PracticeLibraryLanding from "@/pages/PracticeLibraryLanding";
import ChallengerLanding from "@/pages/ChallengerLanding";
import PracticePaper from "@/pages/PracticePaper";
import StudentDashboard from "@/pages/StudentDashboard";
import ParentDashboard from "@/pages/ParentDashboard";
import AIChat from "@/pages/AIChat";
import TutorCollaboration from "@/pages/TutorCollaboration";
import TutorDashboard from "@/pages/TutorDashboard";
import TutorLandingPage from "@/pages/TutorLandingPage";
import TutorPapers from "@/pages/TutorPapers";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminQuestionBanks from "@/pages/AdminQuestionBanks";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminUserApproval from "@/pages/AdminUserApproval";
import AdminPapersManagement from "@/pages/AdminPapersManagement";
import ChallengerMode from "@/pages/ChallengerMode";
import HomePage from "@/pages/HomePage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import AboutUs from "@/pages/AboutUs";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import Resources from "@/pages/Resources";
import QRCodePage from "@/pages/QRCodePage";
import ELibrary from "@/pages/ELibrary";
import ELibraryReading from "@/pages/ELibraryReading";
import ELibraryVocabulary from "@/pages/ELibraryVocabulary";
import STEMLibrary from "@/pages/STEMLibrary";
import EdsWhiteboardX from "@/pages/EdsWhiteboardX";
import AssessmentFactory from "@/pages/AssessmentFactory";
import AdaptiveMock from "@/pages/AdaptiveMock";
import CreateChallenge from "@/pages/CreateChallenge";
import AdminFeedback from "@/pages/AdminFeedback";
import LearningEngine from "@/pages/LearningEngine";
import StudentJourney from "@/pages/StudentJourney";
import ReadingChallenge from "@/pages/ReadingChallenge";
import EdpiesPathways from "@/pages/EdpiesPathways";
import PathwaysAdmin from "@/pages/PathwaysAdmin";
import BrandGuidelines from "@/pages/BrandGuidelines";
import EdsALevelHub from "@/pages/EdsALevelHub";
import KS2Assessment from "@/pages/KS2Assessment";
import WelcomeScreen from "@/pages/WelcomeScreen";
import StudentHome from "@/pages/StudentHome";
import PracticeHub from "@/pages/PracticeHub";
import StudentProfilePage from "@/pages/StudentProfilePage";
import GcseStemBrowser from "@/pages/GcseStemBrowser";
import BoardPracticeBrowser from "@/pages/BoardPracticeBrowser";
import UnifiedDiagnostic from "@/pages/UnifiedDiagnostic";
import ExploreLanding from "@/pages/ExploreLanding";
import PersonalizedHub from "@/pages/PersonalizedHub";
import LearningPathGrid from "@/pages/LearningPathGrid";
import LearningTask from "@/pages/LearningTask";
import WeekDetailView from "@/pages/WeekDetailView";
import LearningModule from "@/pages/LearningModule";
import AdaptiveLearning from "@/pages/AdaptiveLearning";
import WorksheetView from "@/pages/WorksheetView";
import ResourceHub from "@/pages/ResourceHub";
import MarkMyWork from "@/pages/MarkMyWork";
import SuttonStage2 from "@/pages/SuttonStage2";
import ResourceTopic from "@/pages/ResourceTopic";
import WritingWhiteboard from "@/pages/WritingWhiteboard";
import PersonalStatementReview from "@/pages/PersonalStatementReview";
import CustomPracticeGenerator from "@/pages/CustomPracticeGenerator";
import CustomPracticePaper from "@/pages/CustomPracticePaper";
import PersonalizedLearningPath from "@/pages/PersonalizedLearningPath";
import AIHub from "@/pages/AIHub";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Test account emails (keep existing flow for E2E testing)
const TEST_ACCOUNTS = [
  'test@test.com',
  'test@example.com',
  'e2e@test.com',
  'admin@test.com',
  'demo@edpies.com'
];

function App() {
  const [progress, setProgress] = useState(() => {
    const saved = localStorage.getItem('studentProgress');
    const user = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    // Only authenticate if ALL required items exist
    if (saved) {
      return JSON.parse(saved);
    }
    if (user && token) {
      return { user: JSON.parse(user), isAuthenticated: true };
    }
    return null;
  });

  useEffect(() => {
    if (progress) {
      localStorage.setItem('studentProgress', JSON.stringify(progress));
    } else {
      // Ensure everything is cleared when progress is null
      localStorage.removeItem('studentProgress');
    }
  }, [progress]);

  const updateProgress = (updates) => {
    if (updates === null) {
      setProgress(null);
      localStorage.removeItem('studentProgress');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      return;
    }
    setProgress(prev => {
      const updated = { ...prev, ...updates };
      return updated;
    });
  };

  const isAuthenticated = progress?.isAuthenticated || false;
  const userType = progress?.user?.userType || 'student';
  
  // Check if user is a test account (bypass new flow for E2E testing)
  const isTestAccount = TEST_ACCOUNTS.includes(progress?.user?.email?.toLowerCase());
  
  // Check diagnostic status
  const hasCompletedDiagnostic = progress?.hasCompletedDiagnostic || false;
  const hasSeenWelcome = progress?.hasSeenWelcome || false;
  
  // Determine where authenticated users should go
  const getPostAuthRoute = () => {
    // Test accounts bypass new flow
    if (isTestAccount) return '/categories';
    // Admin users go to admin panel
    if (progress?.user?.isAdmin) return '/admin/tutors';
    // Tutor users go to tutor dashboard
    if (userType === 'tutor') return '/tutor-dashboard';
    // Parent users go to parent dashboard
    if (userType === 'parent') return '/parent-dashboard';
    // Students: check diagnostic status
    if (hasCompletedDiagnostic) return '/learning-path';
    if (hasSeenWelcome) return '/explore';
    return '/welcome';
  };

  // New Seneca-style StudentHome dashboard is now the canonical /welcome for
  // every student stage (7+, 11+, GCSE, KS3). Non-student users (parents,
  // tutors, admins) continue to hit the legacy WelcomeScreen so their existing
  // dashboards / role-specific menus stay intact.
  const welcomeRouteElement = (() => {
    const u = progress?.user || {};
    const userType = (u.userType || 'student').toLowerCase();
    return userType === 'student'
      ? <StudentHome progress={progress} updateProgress={updateProgress} />
      : <WelcomeScreen progress={progress} updateProgress={updateProgress} />;
  })();

  return (
    <div className="App">
      <BrowserRouter>
        <SessionControlProvider>
          <ScrollToTop />
          <ImpersonationBanner />
          {/* Persistent XP / Level chip for any authenticated route */}
          <XpLevelBadge />
          <SessionControlUI />
          <Routes>
          {/* Public Homepage */}
          <Route 
            path="/" 
            element={<HomePage isAuthenticated={isAuthenticated} progress={progress} />} 
          />
          <Route 
            path="/about" 
            element={<AboutUs />} 
          />
          <Route 
            path="/resources" 
            element={<Resources isAuthenticated={isAuthenticated} />} 
          />
          <Route 
            path="/resources/:stageId" 
            element={<Resources isAuthenticated={isAuthenticated} />} 
          />
          <Route 
            path="/contact" 
            element={<Contact />} 
          />
          <Route 
            path="/faq" 
            element={<FAQ />} 
          />
          <Route 
            path="/qr-code" 
            element={<QRCodePage />} 
          />
          <Route 
            path="/auth" 
            element={isAuthenticated ? (
              <Navigate to={getPostAuthRoute()} />
            ) : <Auth setProgress={setProgress} />} 
          />
          <Route 
            path="/verify-email" 
            element={<VerifyEmail />} 
          />
          <Route 
            path="/subscription" 
            element={isAuthenticated ? <Subscription progress={progress} updateProgress={updateProgress} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/subscription/success" 
            element={isAuthenticated ? <Subscription progress={progress} updateProgress={updateProgress} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/favorites" 
            element={isAuthenticated ? <Favorites progress={progress} updateProgress={updateProgress} /> : <Navigate to="/auth" />} 
          />
          {/* New Onboarding Flow Routes */}
          <Route 
            path="/welcome" 
            element={isAuthenticated ? welcomeRouteElement : <Navigate to="/auth" />} 
          />
          <Route 
            path="/home" 
            element={isAuthenticated ? <StudentHome progress={progress} updateProgress={updateProgress} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/practice-hub" 
            element={isAuthenticated ? <PracticeHub progress={progress} updateProgress={updateProgress} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/profile" 
            element={isAuthenticated ? <StudentProfilePage progress={progress} updateProgress={updateProgress} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/gcse-stem" 
            element={isAuthenticated ? <GcseStemBrowser progress={progress} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/practice/board/:board" 
            element={isAuthenticated ? <BoardPracticeBrowser /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/practice/board/:board/:stage" 
            element={isAuthenticated ? <BoardPracticeBrowser /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/unified-diagnostic" 
            element={<Navigate to="/ks2-assessment" replace />} 
          />
          <Route 
            path="/explore" 
            element={isAuthenticated ? <ExploreLanding progress={progress} updateProgress={updateProgress} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/personalized-hub" 
            element={isAuthenticated ? <PersonalizedHub progress={progress} updateProgress={updateProgress} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/learning-path" 
            element={isAuthenticated ? <LearningPathGrid progress={progress} updateProgress={updateProgress} /> : <Navigate to="/auth" />} 
          />
          {/* Universal Focus-Mode learning task: powers every "Start Task"
              tap from the 16-week plan, age-aware via the student profile. */}
          <Route
            path="/learn/:type/:subject/:topic"
            element={isAuthenticated ? <LearningTask /> : <Navigate to="/auth" />}
          />
          <Route 
            path="/learning-path/week/:weekNumber" 
            element={isAuthenticated ? <WeekDetailView progress={progress} updateProgress={updateProgress} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/learning-path/week/:weekNumber/module/:moduleIndex" 
            element={isAuthenticated ? <LearningModule progress={progress} updateProgress={updateProgress} /> : <Navigate to="/auth" />} 
          />
          {/* 16-Week Adaptive Learning System — page is browseable for all (locked preview), worksheets are gated */}
          <Route 
            path="/adaptive" 
            element={isAuthenticated ? <AdaptiveLearning /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/adaptive/worksheet/:worksheetId" 
            element={isAuthenticated ? (
              <GatedRoute capability="adaptive" requiredTier="advance" featureName="Adaptive Worksheets">
                <WorksheetView />
              </GatedRoute>
            ) : <Navigate to="/auth" />} 
          />
          {/* Resources Hub */}
          <Route 
            path="/resources-hub" 
            element={isAuthenticated ? <ResourceHub progress={progress} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/mark-my-work" 
            element={isAuthenticated ? <MarkMyWork /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/sutton-stage2" 
            element={isAuthenticated ? <SuttonStage2 /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/resources/maths/:categoryId/:topicId" 
            element={isAuthenticated ? <ResourceTopic subject="maths" /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/resources/english/:categoryId/:topicId" 
            element={isAuthenticated ? <ResourceTopic subject="english" /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/resources/physics/:categoryId/:topicId" 
            element={isAuthenticated ? <ResourceTopic subject="physics" /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/resources/maths-7plus/:categoryId/:topicId" 
            element={isAuthenticated ? <ResourceTopic subject="maths-7plus" /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/resources/english-7plus/:categoryId/:topicId" 
            element={isAuthenticated ? <ResourceTopic subject="english-7plus" /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/resources/kumon-7plus/:categoryId/:topicId" 
            element={isAuthenticated ? <ResourceTopic subject="kumon-7plus" /> : <Navigate to="/auth" />} 
          />
          {/* AI Writing Coach */}
          <Route 
            path="/writing-coach" 
            element={isAuthenticated ? <WritingWhiteboard /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/personal-statement-review" 
            element={isAuthenticated ? <PersonalStatementReview /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/student-dashboard" 
            element={isAuthenticated ? <StudentDashboard progress={progress} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/parent-dashboard" 
            element={isAuthenticated ? <ParentDashboard progress={progress} updateProgress={updateProgress} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/ai-chat" 
            element={isAuthenticated ? <AIChat progress={progress} /> : <Navigate to="/auth" />} 
          />
          {/* Tutor Papers - Student View */}
          <Route 
            path="/tutor-papers" 
            element={isAuthenticated ? (
              <GatedRoute capability="tutor_papers" requiredTier="pro" featureName="Tutor Papers">
                <TutorPapers />
              </GatedRoute>
            ) : <Navigate to="/auth" />} 
          />
          {/* E-Library Routes - Eds-Library */}
          <Route 
            path="/elibrary" 
            element={isAuthenticated ? <ELibrary progress={progress} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/elibrary/read/:passageId" 
            element={isAuthenticated ? <ELibraryReading /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/elibrary/vocabulary" 
            element={isAuthenticated ? <ELibraryVocabulary /> : <Navigate to="/auth" />} 
          />
          {/* STEM Library - KS3/GCSE */}
          <Route 
            path="/stem-library" 
            element={isAuthenticated ? <STEMLibrary /> : <Navigate to="/auth" />} 
          />
          {/* Eds Whiteboard X Routes */}
          <Route 
            path="/whiteboard-x" 
            element={isAuthenticated ? <EdsWhiteboardX /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/assessment-factory" 
            element={isAuthenticated ? <AssessmentFactory /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/adaptive-mock" 
            element={isAuthenticated ? <AdaptiveMock /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/my-journey" 
            element={<Navigate to="/ks2-assessment" replace />} 
          />
          <Route 
            path="/ks2-assessment" 
            element={isAuthenticated ? <KS2Assessment /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/learning-engine" 
            element={isAuthenticated ? <LearningEngine /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/learning-engine/:stage/:subject" 
            element={isAuthenticated ? <LearningEngine /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/reading-challenge/:stage" 
            element={isAuthenticated ? <ReadingChallenge /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/pathways" 
            element={isAuthenticated ? <EdpiesPathways /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/pathways/admin" 
            element={isAuthenticated ? <PathwaysAdmin /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/whiteboard-x/create" 
            element={isAuthenticated && userType === 'tutor' ? <CreateChallenge /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/tutor-collaboration" 
            element={isAuthenticated ? <TutorCollaboration /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/become-a-tutor" 
            element={<TutorLandingPage />} 
          />
          <Route 
            path="/tutor/dashboard" 
            element={isAuthenticated && userType === 'tutor' ? <TutorDashboard /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/tutor-dashboard" 
            element={<Navigate to="/tutor/dashboard" />}
          />
          <Route 
            path="/admin/questions" 
            element={isAuthenticated && userType === 'tutor' ? <Navigate to="/tutor/dashboard" /> : <Navigate to="/" />} 
          />
          <Route 
            path="/admin/tutors" 
            element={isAuthenticated ? <AdminDashboard updateProgress={updateProgress} /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/admin/question-banks" 
            element={isAuthenticated ? <AdminQuestionBanks /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/admin/papers" 
            element={isAuthenticated ? <AdminPapersManagement /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/admin/analytics" 
            element={isAuthenticated ? <AdminAnalytics /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/admin/feedback" 
            element={isAuthenticated ? <AdminFeedback /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/admin/users" 
            element={isAuthenticated ? <AdminUserApproval /> : <Navigate to="/auth" />} 
          />
          {/* Main Categories Dashboard - Not for Parents or Admins */}
          <Route 
            path="/categories" 
            element={isAuthenticated ? (
              progress?.user?.isAdmin ? <Navigate to="/admin/tutors" /> :
              userType === 'parent' ? <Navigate to="/parent-dashboard" /> : 
              <CategorySelection progress={progress} updateProgress={updateProgress} />
            ) : <Navigate to="/auth" />} 
          />
          <Route 
            path="/subjects/:categoryId" 
            element={isAuthenticated ? (userType === 'parent' ? <Navigate to="/parent-dashboard" /> : <SubjectDashboard progress={progress} updateProgress={updateProgress} />) : <Navigate to="/auth" />} 
          />
          <Route 
            path="/a-level-hub" 
            element={isAuthenticated ? <EdsALevelHub /> : <Navigate to="/auth" />} 
          />
          <Route 
            path="/practice-library" 
            element={isAuthenticated ? (userType === 'parent' ? <Navigate to="/parent-dashboard" /> : <PracticeLibraryLanding progress={progress} />) : <Navigate to="/auth" />} 
          />
          <Route 
            path="/challenger-hub" 
            element={isAuthenticated ? (userType === 'parent' ? <Navigate to="/parent-dashboard" /> : (
              <GatedRoute capability="challenger" requiredTier="pro" featureName="Challenger Series">
                <ChallengerLanding progress={progress} />
              </GatedRoute>
            )) : <Navigate to="/auth" />} 
          />
          <Route 
            path="/11plus-patterns" 
            element={isAuthenticated ? (userType === 'parent' ? <Navigate to="/parent-dashboard" /> : <ElevenPlusPatterns progress={progress} updateProgress={updateProgress} />) : <Navigate to="/auth" />} 
          />
          <Route 
            path="/papers/:stage/:subject" 
            element={isAuthenticated ? (userType === 'parent' ? <Navigate to="/parent-dashboard" /> : <PaperLibrary progress={progress} />) : <Navigate to="/auth" />} 
          />
          <Route 
            path="/paper/:stage/:subject/:paperType/:paperNumber" 
            element={isAuthenticated ? (userType === 'parent' ? <Navigate to="/parent-dashboard" /> : <PracticePaper progress={progress} />) : <Navigate to="/auth" />} 
          />
          <Route 
            path="/ai-practice" 
            element={isAuthenticated ? (userType === 'parent' ? <Navigate to="/parent-dashboard" /> : (
              <GatedRoute capability="growth_zone" requiredTier="advance" featureName="My Growth Zone">
                <CustomPracticeGenerator />
              </GatedRoute>
            )) : <Navigate to="/auth" />} 
          />
          <Route 
            path="/ai-practice/:paperId" 
            element={isAuthenticated ? (userType === 'parent' ? <Navigate to="/parent-dashboard" /> : (
              <GatedRoute capability="growth_zone" requiredTier="advance" featureName="My Growth Zone">
                <CustomPracticePaper />
              </GatedRoute>
            )) : <Navigate to="/auth" />} 
          />
          <Route 
            path="/my-learning-path" 
            element={isAuthenticated ? (userType === 'parent' ? <Navigate to="/parent-dashboard" /> : <PersonalizedLearningPath />) : <Navigate to="/auth" />} 
          />
          <Route 
            path="/ai-hub" 
            element={isAuthenticated ? (userType === 'parent' ? <Navigate to="/parent-dashboard" /> : <AIHub />) : <Navigate to="/auth" />} 
          />
          <Route 
            path="/challenger/:stage/:subject" 
            element={isAuthenticated ? (userType === 'parent' ? <Navigate to="/parent-dashboard" /> : (
              <GatedRoute capability="challenger" requiredTier="pro" featureName="Challenger Series">
                <ChallengerMode progress={progress} />
              </GatedRoute>
            )) : <Navigate to="/auth" />} 
          />
          <Route 
            path="/11plus" 
            element={
              progress ? <Navigate to="/11plus/dashboard" /> : <Onboarding setProgress={setProgress} />
            } 
          />
          <Route 
            path="/11plus/diagnostic" 
            element={<Navigate to="/ks2-assessment" replace />} 
          />
          <Route 
            path="/diagnostic/:sessionId" 
            element={<Navigate to="/ks2-assessment" replace />} 
          />
          <Route 
            path="/learning-plan" 
            element={<Navigate to="/learning-engine" replace />} 
          />
          <Route 
            path="/11plus/dashboard" 
            element={<Dashboard progress={progress} updateProgress={updateProgress} />} 
          />
          <Route 
            path="/exam/:stage/:subject" 
            element={<MockExam progress={progress} updateProgress={updateProgress} />} 
          />
          <Route 
            path="/results" 
            element={<Results progress={progress} />} 
          />
          <Route 
            path="/privacy" 
            element={<PrivacyPolicy />} 
          />
          <Route 
            path="/terms" 
            element={<TermsOfService />} 
          />
          <Route 
            path="/brand" 
            element={<BrandGuidelines />} 
          />
        </Routes>
        </SessionControlProvider>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;
